import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { DsBadge, DsButton, DsSpinner } from "../../../design-system";
import { assetService } from "../../../services/api";
import { REQUEST_STATUSES, inputSm } from "./constants";

function normalizeList(res) {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.requests)) return d.requests;
  if (Array.isArray(d?.rows)) return d.rows;
  return [];
}

export default function AssetRequestsPanel({ canManage, initialFilters = {} }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState(initialFilters.status || "pending");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status && status !== "all") params.status = status;
      const res = await assetService.listRequests(params);
      setRows(normalizeList(res));
    } catch (err) {
      setRows([]);
      toast.error(err?.response?.data?.message || "Failed to load requests");
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (initialFilters.status) setStatus(initialFilters.status);
  }, [initialFilters.status]);

  const run = async (id, fn, okMsg) => {
    if (!canManage) {
      toast.error("No permission to manage requests");
      return;
    }
    setBusyId(id);
    try {
      await fn(id);
      toast.success(okMsg);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Action failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="ds-h1 text-[hsl(var(--ds-fg))]">Asset Requests</h1>
        <p className="ds-caption mt-1 text-[hsl(var(--ds-muted-fg))]">
          Approve, reserve, assign, or reject employee asset requests.
        </p>
      </div>

      <div className="ds-page-toolbar flex flex-wrap items-center gap-2 rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-3">
        <select className={`${inputSm} max-w-[12rem]`} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="all">All</option>
          {REQUEST_STATUSES.map((s) => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
        <DsButton type="button" variant="outline" onClick={load}>Refresh</DsButton>
      </div>

      <div className="ds-page-panel overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]">
        {loading ? (
          <div className="flex justify-center py-12"><DsSpinner /></div>
        ) : rows.length === 0 ? (
          <p className="ds-caption p-6 text-[hsl(var(--ds-muted-fg))]">No requests.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] text-left">
                  <th className="px-3 py-2 ds-caption">Request</th>
                  <th className="px-3 py-2 ds-caption">Employee</th>
                  <th className="px-3 py-2 ds-caption">Status</th>
                  <th className="px-3 py-2 ds-caption">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const emp =
                    row.employeeName ||
                    row.employee_name ||
                    row.requestedByName ||
                    "—";
                  const title =
                    row.title ||
                    row.typeName ||
                    row.categoryName ||
                    row.requestNo ||
                    `#${row.id}`;
                  const st = row.status || "pending";
                  const busy = busyId === row.id;
                  return (
                    <tr key={row.id} className="border-b border-[hsl(var(--ds-border))] last:border-0">
                      <td className="px-3 py-2">
                        <div className="font-medium text-[hsl(var(--ds-fg))]">{title}</div>
                        <div className="ds-caption text-[hsl(var(--ds-muted-fg))]">
                          {(row.createdAt || row.created_at || "").slice(0, 10) || "—"}
                          {row.notes ? ` · ${row.notes}` : ""}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-[hsl(var(--ds-fg))]">{emp}</td>
                      <td className="px-3 py-2"><DsBadge>{st}</DsBadge></td>
                      <td className="px-3 py-2">
                        {canManage && ["pending", "approved", "reserved"].includes(st) ? (
                          <div className="flex flex-wrap gap-1">
                            {st === "pending" ? (
                              <>
                                <DsButton
                                  size="sm"
                                  disabled={busy}
                                  onClick={() =>
                                    run(row.id, assetService.approveRequest, "Approved")
                                  }
                                >
                                  Approve
                                </DsButton>
                                <DsButton
                                  size="sm"
                                  variant="outline"
                                  disabled={busy}
                                  onClick={() =>
                                    run(row.id, assetService.reserveRequest, "Reserved")
                                  }
                                >
                                  Reserve
                                </DsButton>
                                <DsButton
                                  size="sm"
                                  variant="danger"
                                  disabled={busy}
                                  onClick={() =>
                                    run(row.id, assetService.rejectRequest, "Rejected")
                                  }
                                >
                                  Reject
                                </DsButton>
                              </>
                            ) : null}
                            {(st === "approved" || st === "reserved") ? (
                              <DsButton
                                size="sm"
                                disabled={busy}
                                onClick={() =>
                                  run(row.id, assetService.assignRequest, "Assigned")
                                }
                              >
                                Assign
                              </DsButton>
                            ) : null}
                          </div>
                        ) : (
                          <span className="ds-caption text-[hsl(var(--ds-muted-fg))]">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
